import { useState } from'react';
import { motion, AnimatePresence } from'framer-motion';

interface Sefirah {
 id: number;
 name: string;
 hebrew: string;
 transliteration: string;
 meaning: string;
 color: string;
 position: { x: number; y: number };
 description: string;
 attribute: string;
}

const sefirot: Sefirah[] = [
 {
 id: 0,
 name:'Crown',
 hebrew:'כתר',
 transliteration:'Keter',
 meaning:'Will/Crown',
 color:'#FFFFFF',
 position: { x: 50, y: 5 },
 description:'The divine will and infinite potential',
 attribute:'Supernal consciousness',
 },
 {
 id: 1,
 name:'Wisdom',
 hebrew:'חכמה',
 transliteration:'Chochmah',
 meaning:'Wisdom',
 color:'#3B82F6',
 position: { x: 20, y: 20 },
 description:'The first flash of insight',
 attribute:'Intuitive knowledge',
 },
 {
 id: 2,
 name:'Understanding',
 hebrew:'בינה',
 transliteration:'Binah',
 meaning:'Understanding',
 color:'#10B981',
 position: { x: 80, y: 20 },
 description:'Contemplation and analysis',
 attribute:'Rational comprehension',
 },
 {
 id: 3,
 name:'Loving-kindness',
 hebrew:'חסד',
 transliteration:'Chesed',
 meaning:'Mercy',
 color:'#60A5FA',
 position: { x: 20, y: 40 },
 description:'Unlimited giving and expansion',
 attribute:'Benevolence',
 },
 {
 id: 4,
 name:'Strength',
 hebrew:'גבורה',
 transliteration:'Gevurah',
 meaning:'Power/Judgment',
 color:'#EF4444',
 position: { x: 80, y: 40 },
 description:'Restraint and discipline',
 attribute:'Discernment',
 },
 {
 id: 5,
 name:'Beauty',
 hebrew:'תפארת',
 transliteration:'Tiferet',
 meaning:'Harmony',
 color:'#FBBF24',
 position: { x: 50, y: 50 },
 description:'Balance between mercy and judgment',
 attribute:'Compassion',
 },
 {
 id: 6,
 name:'Victory',
 hebrew:'נצח',
 transliteration:'Netzach',
 meaning:'Eternity',
 color:'#F59E0B',
 position: { x: 20, y: 70 },
 description:'Persistence and ambition',
 attribute:'Endurance',
 },
 {
 id: 7,
 name:'Splendor',
 hebrew:'הוד',
 transliteration:'Hod',
 meaning:'Glory',
 color:'#EC4899',
 position: { x: 80, y: 70 },
 description:'Humility and surrender',
 attribute:'Acknowledgment',
 },
 {
 id: 8,
 name:'Foundation',
 hebrew:'יסוד',
 transliteration:'Yesod',
 meaning:'Connection',
 color:'#8B5CF6',
 position: { x: 50, y: 85 },
 description:'Channel of divine flow',
 attribute:'Bonding',
 },
 {
 id: 9,
 name:'Kingdom',
 hebrew:'מלכות',
 transliteration:'Malchut',
 meaning:'Sovereignty',
 color:'#7C3AED',
 position: { x: 50, y: 100 },
 description:'Divine presence in the world',
 attribute:'Manifestation',
 },
];

const connections: [number, number][] = [
 [0, 1], [0, 2], [1, 2], [1, 3], [1, 5],
 [2, 3], [2, 4], [2, 5], [3, 4], [3, 5],
 [4, 5], [3, 6], [4, 7], [5, 6], [5, 7],
 [5, 8], [6, 7], [6, 8], [7, 8], [8, 9],
];

export function SefirotTree({ className ='' }: { className?: string }) {
 const [selectedSefirah, setSelectedSefirah] = useState<number | null>(null);
 const [hoveredSefirah, setHoveredSefirah] = useState<number | null>(null);

 const isConnected = (id1: number, id2: number): boolean => {
 return connections.some(
 ([a, b]) => (a === id1 && b === id2) || (a === id2 && b === id1)
 );
 };

 const getHighlightedConnections = (): [number, number][] => {
 if (selectedSefirah === null && hoveredSefirah === null) return [];
 const activeId = selectedSefirah !== null ? selectedSefirah : hoveredSefirah;
 return connections.filter(([a, b]) => a === activeId || b === activeId);
 };

 const highlightedConnections = getHighlightedConnections();

 return (
 <div className={`relative ${className}`}>
 <div className="mb-6 text-center">
 <h3 className="text-2xl font-bold text-white mb-2">The Sefirot Tree</h3>
 <p className="text-sm text-slate-400">
 Click on any Sefirah to explore its attributes
 </p>
 </div>

 {/* Tree Visualization */}
 <div className="relative w-full max-w-3xl mx-auto aspect-[3/4] bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-slate-700 p-8">
 {/* Background divine light */}
 <div className="absolute inset-0 opacity-10">
 <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full bg-white blur-3xl" />
 </div>

 {/* Connection Lines */}
 <svg className="absolute inset-0 w-full h-full">
 {connections.map(([from, to], idx) => {
 const isHighlighted = highlightedConnections.some(
 ([a, b]) => (a === from && b === to) || (a === to && b === from)
 );
 const fromSefirah = sefirot[from];
 const toSefirah = sefirot[to];

 return (
 <motion.line
 key={idx}
 x1={`${fromSefirah.position.x}%`}
 y1={`${fromSefirah.position.y}%`}
 x2={`${toSefirah.position.x}%`}
 y2={`${toSefirah.position.y}%`}
 stroke={isHighlighted ?'#FBBF24' :'#475569'}
 strokeWidth={isHighlighted ? 3 : 1}
 strokeOpacity={isHighlighted ? 1 : 0.3}
 initial={{ pathLength: 0 }}
 animate={{ pathLength: 1 }}
 transition={{ duration: 1, delay: idx * 0.05 }}
 />
 );
 })}
 </svg>

 {/* Sefirot Nodes */}
 {sefirot.map((sefirah) => {
 const isSelected = selectedSefirah === sefirah.id;
 const isHovered = hoveredSefirah === sefirah.id;
 const isConnectedToActive =
 selectedSefirah !== null && isConnected(sefirah.id, selectedSefirah);
 const isActive = isSelected || isHovered || isConnectedToActive;

 return (
 <motion.button
 key={sefirah.id}
 className="absolute"
 style={{
 left: `${sefirah.position.x}%`,
 top: `${sefirah.position.y}%`,
 transform:'translate(-50%, -50%)',
 }}
 onClick={() =>
 setSelectedSefirah(selectedSefirah === sefirah.id ? null : sefirah.id)
 }
 onMouseEnter={() => setHoveredSefirah(sefirah.id)}
 onMouseLeave={() => setHoveredSefirah(null)}
 whileHover={{ scale: 1.15 }}
 whileTap={{ scale: 0.95 }}
 initial={{ opacity: 0, scale: 0 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ delay: sefirah.id * 0.1 }}
 >
 {/* Glow effect */}
 {isActive && (
 <motion.div
 className="absolute inset-0 rounded-full blur-xl"
 style={{ backgroundColor: sefirah.color }}
 initial={{ opacity: 0 }}
 animate={{ opacity: 0.4 }}
 transition={{ duration: 0.3 }}
 />
 )}

 {/* Node circle */}
 <div
 className="relative w-20 h-20 rounded-full flex flex-col items-center justify-center transition-all duration-300 border-2"
 style={{
 backgroundColor: isActive ? `${sefirah.color}40` : `${sefirah.color}20`,
 borderColor: isActive ? sefirah.color : `${sefirah.color}60`,
 boxShadow: isActive ? `0 0 20px ${sefirah.color}80` :'none',
 }}
 >
 <div
 className="hebrew-text text-lg font-bold"
 style={{ color: sefirah.color }}
 >
 {sefirah.hebrew}
 </div>
 <div className="text-[9px] text-slate-300 mt-1 font-medium">
 {sefirah.transliteration}
 </div>
 </div>
 </motion.button>
 );
 })}
 </div>

 {/* Info Panel */}
 <AnimatePresence>
 {selectedSefirah !== null && (
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 exit={{ opacity: 0, y: -20 }}
 className="mt-6 p-6 rounded-xl border"
 style={{
 backgroundColor: `${sefirot[selectedSefirah].color}15`,
 borderColor: `${sefirot[selectedSefirah].color}40`,
 }}
 >
 <div className="flex items-start justify-between mb-4">
 <div>
 <h3 className="text-2xl font-bold text-white mb-1">
 {sefirot[selectedSefirah].name}
 </h3>
 <p
 className="hebrew-text text-xl mb-1"
 style={{ color: sefirot[selectedSefirah].color }}
 >
 {sefirot[selectedSefirah].hebrew}
 </p>
 <p className="text-sm text-slate-400 italic">
 {sefirot[selectedSefirah].transliteration} — {sefirot[selectedSefirah].meaning}
 </p>
 </div>
 <button
 onClick={() => setSelectedSefirah(null)}
 className="text-slate-400 hover:text-white text-2xl"
 >
 ✕
 </button>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
 <div
 className="p-3 rounded-lg"
 style={{ backgroundColor: `${sefirot[selectedSefirah].color}20` }}
 >
 <div className="text-xs text-slate-400 mb-1">Description</div>
 <div className="text-sm text-slate-200">
 {sefirot[selectedSefirah].description}
 </div>
 </div>
 <div
 className="p-3 rounded-lg"
 style={{ backgroundColor: `${sefirot[selectedSefirah].color}20` }}
 >
 <div className="text-xs text-slate-400 mb-1">Attribute</div>
 <div className="text-sm text-slate-200">
 {sefirot[selectedSefirah].attribute}
 </div>
 </div>
 </div>

 {/* Connected Sefirot */}
 <div>
 <div className="text-xs text-slate-400 mb-2">Connected to:</div>
 <div className="flex flex-wrap gap-2">
 {connections
 .filter(([a, b]) => a === selectedSefirah || b === selectedSefirah)
 .map(([a, b]) => {
 const connectedId = a === selectedSefirah ? b : a;
 const connectedSefirah = sefirot[connectedId];
 return (
 <button
 key={connectedId}
 onClick={() => setSelectedSefirah(connectedId)}
 className="px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105"
 style={{
 backgroundColor: `${connectedSefirah.color}30`,
 color: connectedSefirah.color,
 border: `1px solid ${connectedSefirah.color}60`,
 }}
 >
 {connectedSefirah.name}
 </button>
 );
 })}
 </div>
 </div>
 </motion.div>
 )}
 </AnimatePresence>

 {/* Legend */}
 <div className="mt-6 p-4 rounded-xl bg-slate-800/50 border border-slate-700">
 <div className="text-xs text-slate-400 mb-3 text-center">
 The Ten Sefirot represent divine emanations through which the infinite becomes manifest
 </div>
 <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-5 gap-2">
 {sefirot.map((sefirah) => (
 <button
 key={sefirah.id}
 onClick={() => setSelectedSefirah(sefirah.id)}
 className="p-2 rounded-lg border transition-all duration-200 hover:scale-105"
 style={{
 backgroundColor: `${sefirah.color}15`,
 borderColor: selectedSefirah === sefirah.id ? sefirah.color : `${sefirah.color}30`,
 }}
 >
 <div
 className="hebrew-text text-xs font-bold mb-1"
 style={{ color: sefirah.color }}
 >
 {sefirah.hebrew}
 </div>
 <div className="text-[9px] text-slate-400">{sefirah.name}</div>
 </button>
 ))}
 </div>
 </div>
 </div>
 );
}
